import {useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {useLoadableValueOrDefault} from "./jotaiHooks";
import {Field} from "../types/domain/field";
import {getAllFields} from "../services/fieldService";
import {swallowException} from "../utils/appUtils";
import {activeCompanyResultsLoadable} from "../store/activeCompanyResultsStore";
import {
    isCropGrowthAlert,
    isSoilMoistureAlert,
    normalizeCropGrowth,
    normalizeSoilMoisture,
} from "../utils/insightsUtils";
import {FieldResultStatistics} from "../types/domain/fieldResult";
import {activeCompanyFieldsAtom, activeCompanyFieldsLoadable} from "../store/activeCompanyFieldsStore";
import {activeMergedFarmIds} from "../store/activeMergedFarmIds";

export interface FieldStatus extends FieldResultStatistics {
    isSoilMoistureAlert: boolean;
    isCropGrowthAlert: boolean;
}

export const useFields = ({
    companyId,
    orderId,
    farmId,
}: {
    companyId: string | null;
    orderId?: string;
    farmId?: string;
}) => {
    const [items, setItems] = useState<Field[]>([]);
    const [counter, setCounter] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let ignore = false;

        setItems([]);
        if (companyId) {
            setIsLoading(true);
            getAllFields({companyId, orderId, farmId})
                .then((fields) => {
                    if (!ignore) {
                        setItems(fields.sort(sortFields));
                        setIsLoading(false);
                    }
                })
                .catch(swallowException);
        }
        return () => {
            ignore = true;
        };
    }, [companyId, orderId, farmId, counter]);

    const refresh = () => {
        setCounter((count) => count + 1);
    };

    return {items, isLoading, refresh};
};

export const useMultiFarmFields = ({companyId}: {companyId: string}) => {
    const [activeMergedFarmIdsAtom] = useAtom(activeMergedFarmIds);
    const [items, setItems] = useState<Field[]>([]);

    useEffect(() => {
        if (!companyId || activeMergedFarmIdsAtom.length === 0) return;
        void Promise.all(activeMergedFarmIdsAtom.map((farmId) => getAllFields({companyId, farmId}))).then((res) => {
            setItems(res.flat());
        });
    }, [companyId, activeMergedFarmIdsAtom]);

    return items;
};

export const useFieldStatusById = () => {
    const activeCompanyResultsState = useAtomValue(activeCompanyResultsLoadable);
    const fields = useActiveCompanyFields();

    const moistureAlertThreshold = (id: string) => {
        const field = fields.items.find((item) => item.id === id);
        return field?.irrigationCriteria ?? 0.1;
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    return useMemo(() => {
        if (activeCompanyResultsState.state === "hasData") {
            return _.mapValues(
                activeCompanyResultsState.data,
                (results: FieldResultStatistics, key) =>
                    ({
                        soilMoisture: normalizeSoilMoisture(results.soilMoisture),
                        cropGrowth: normalizeCropGrowth(results.cropGrowth),
                        timeUntilNextIrrigation: results.timeUntilNextIrrigation,
                        isSoilMoistureAlert: isSoilMoistureAlert(
                            results.soilMoisture,
                            moistureAlertThreshold(key),
                            results.vegetationCover,
                        ),
                        isCropGrowthAlert: isCropGrowthAlert(results.cropGrowth, results.vegetationCover),
                    }) as FieldStatus,
            );
        } else {
            return {};
        }
    }, [activeCompanyResultsState]);
};

const sortFields = (a: Field, b: Field): number => {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
};

export const useActiveCompanyFields = () => {
    const items = useLoadableValueOrDefault(activeCompanyFieldsLoadable, [], (atom) => atom.fields);
    const mapByFieldId = useLoadableValueOrDefault(activeCompanyFieldsLoadable, {}, (atom) => atom.mapByFarmId);
    const refresh = useSetAtom(activeCompanyFieldsAtom);

    return {items, mapByFieldId, refresh};
};
